import {includes} from '@shared/helpers/collection'
import {memoize} from '@shared/helpers/function'

import {DeviceType, OSType, MobileAppType, LockMethodType, MobileApp, Device} from '@web/core/constants'
import getApi from '@web/helpers/api'
import {getCipher} from '@web/helpers/crypto/js'
import {osType, mobileAppType, isMobile} from '@web/helpers/userAgent'

import {PayNextPrepare} from '../interfaces'

import {ApplyOrder, OrderProcessResult, OrdersPrepare, PrepareOrder, PrepareOrderRequest} from './interface'

export type OrderPathType = 'mall' | 'easyBooking' | 'seller' | 'familyPay'

/** 결제 프로세스 api model */
export default class Api {
    private api: ReturnType<typeof getApi>

    private readonly deviceType: DeviceType

    private readonly isMobileDisplay: boolean

    private readonly osType: OSType

    private readonly userAgent: string

    private readonly mobileAppType: MobileAppType

    constructor(baseUrl: string) {
        this.api = getApi(baseUrl)

        /**
         * 결제 프로세스에 필요한 디바이스 정보
         * - 결제를 진행하기 위해서는, 결제에 맞는 디바이스 정보가 필요함
         **/
        const userAgent = navigator.userAgent
        this.deviceType = isMobile ? Device.MOBILE : Device.PC
        this.isMobileDisplay = isMobile
        this.osType = osType
        this.userAgent = userAgent
        this.mobileAppType = mobileAppType
    }

    /**
     * 주문 준비
     */
    async fetchOrderPrepare({
        type,
        verificationKey,
        orderSheetId,
        requestData: prepareOrder,
        deviceLockType,
    }: {
        type: OrderPathType
        verificationKey: string
        orderSheetId: string
        requestData: PrepareOrder
        deviceLockType: LockMethodType
    }): Promise<OrdersPrepare> {
        const apiPath = `/orderSheet/${type}/order/prepare`
        const cipher = getCipher(verificationKey, prepareOrder.payment)

        const request: PrepareOrderRequest = {
            ...prepareOrder,
            cipher,
        }

        const {
            data: {result},
        } = await this.api.post(apiPath, {
            orderSheetId,
            requestData: request,
            deviceType: this.deviceType,
            isMobileDisplay: this.isMobileDisplay,
            osType: this.osType,
            userAgent: this.userAgent,
            ...(includes(this.mobileAppType, [MobileApp.NAVER, MobileApp.NAVER_PAY, MobileApp.NAVER_SHOPPING]) && {
                mobileAppType: this.mobileAppType,
            }),
            deviceLockType,
        })

        return result
    }

    /**
     * 주문 처리
     */
    async beginOrderProcess({type, orderProcessId}: {type: OrderPathType; orderProcessId: string}): Promise<void> {
        const apiPath = `/orderSheet/${type}/order/process`
        await this.api.post(apiPath, {
            orderProcessId,
        })
    }

    /**
     * 주문 처리 상태 조회, 완료될 때 까지 호출
     */
    async fetchOrderProcess({
        type,
        orderProcessId,
        entry,
    }: {
        type: OrderPathType
        orderProcessId: string
        entry: string
    }): Promise<OrderProcessResult> {
        const apiPath = `/orderSheet/${type}/order/process`
        const {
            data: {result},
        } = await this.api.get(apiPath, {params: {orderProcessId, entry}})

        return result
    }

    /**
     * 주문 승인요청
     */
    async applyOrder({
        type,
        orderProcessId,
        applyOrderRequest,
    }: {
        type: OrderPathType
        orderProcessId: string
        applyOrderRequest: ApplyOrder
    }): Promise<void> {
        const apiPath = `/orderSheet/${type}/order/apply`
        await this.api.post(apiPath, {
            orderProcessId,
            applyOrderRequest,
            deviceType: this.deviceType,
            isMobileDisplay: this.isMobileDisplay,
            osType: this.osType,
            userAgent: this.userAgent,
        })
    }

    /** 나중에결제 결제요청 */
    async fetchOrderPrepareNext({
        type,
        verificationKey,
        requestData: prepareOrder,
        deviceLockType,
        orderNo,
    }: {
        type: OrderPathType
        verificationKey: string
        requestData: PrepareOrder
        deviceLockType: LockMethodType
        orderNo: string
    }): Promise<PayNextPrepare> {
        const apiPath = `/orderSheet/${type}/order/prepare`
        const cipher = getCipher(verificationKey, prepareOrder.payment)
        const request: PrepareOrderRequest = {
            ...prepareOrder,
            cipher,
        }
        const {
            data: {result},
        } = await this.api.post(apiPath, {
            orderNo,
            requestData: request,
            deviceType: this.deviceType,
            isMobileDisplay: this.isMobileDisplay,
            osType: this.osType,
            userAgent: this.userAgent,
            ...(this.mobileAppType === MobileApp.NAVER || this.mobileAppType === MobileApp.NAVER_PAY
                ? {mobileAppType: this.mobileAppType}
                : {}),
            deviceLockType,
        })

        return result
    }

    /** 나중에결제 결제 승인 요청 */
    async applyOrderNext({
        type,
        orderNo,
        prepareId,
        arsSessionKey,
    }: {
        type: OrderPathType
        orderNo: string
        prepareId: string
        arsSessionKey?: string
    }): Promise<void> {
        const apiPath = `/orderSheet/${type}/order/apply`
        const {
            data: {result},
        } = await this.api.post(apiPath, {
            orderNo,
            prepareId,
            arsSessionKey,
            deviceType: this.deviceType,
            isMobileDisplay: this.isMobileDisplay,
            osType: this.osType,
            userAgent: this.userAgent,
        })

        return result
    }
}

/** 배송 api 를 생성합니다 */
export const getApis: (baseUrl: string) => Api = memoize((baseUrl: string) => new Api(baseUrl))
